import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { EzDialogComponent } from "@template-ui/components/ez-dialog/ez-dialog.component";
import { EzDialogData } from "@template-ui/types";
import { HearingPhaseBarDirective } from "../hearing-detail/hearing-phase-bar.directive";
import { CommonModule } from "@angular/common";
import { Component, DestroyRef, Input, ViewChild, computed, effect, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { Subject, buffer, forkJoin, of } from "rxjs";
import { catchError, concatMap, debounceTime, filter, finalize, map, tap } from "rxjs/operators";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { TabConfig } from "@template-ui/types";
import { EzFormInputType, InputChangeEvent, InputConfig } from "@ez-library/types/ez-form.types";

import { RoleService } from "@app/shared/services/roles.service";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import { BitsStateService } from "@app/core/services/bits-state.service";
import { HearingCycleContextStore } from "../hearing-detail/hearing-cycle-context.store";
import { PHMS_FACILITY_TERMINOLOGY } from "../phms-facility.terminology";
import { PHMS_HEARING_TERMINOLOGY } from "../phms-hearing.terminology";
import { PHMS_DECISION_TERMINOLOGY } from "../phms-decision.terminology";
import { HearingRecordService } from "./hearing-record.service";
import { CaseContextRefreshService } from "@app/case-context/services/case-context-refresh.service";
import { HearingOfficialAssignmentService, HearingOfficialAssignment } from "../hearing-detail/hearing-official-assignment.service";
import { HearingNoticeWorkspaceService, NoticeParticipant } from "./hearing-notice-workspace.service";
import { PhmsCaseDocumentsComponent } from "../documents/phms-case-documents.component";
import { HearingOffenseSectionComponent } from "./sections/hearing-offense-section.component";
import {
  DecisionFormValues,
  HearingDecisionSectionComponent,
  createEmptyDecisionValues,
} from "./sections/hearing-decision-section.component";
import { HearingTaskWorkspaceComponent } from "./hearing-task-workspace.component";
import {
  HearingAttendanceChange,
  HearingAttendee,
  HearingParticipantsSectionComponent,
} from "./sections/hearing-participants-section.component";
import { HearingTopicGroup, HearingTopicSectionComponent } from "./sections/hearing-topic-section.component";
import {
  AtHearingFormValues,
  FINDING_OPTIONS,
  HearingDecisionFinalizeRequest,
  HearingDecisionOutcome,
  HearingRecordDto,
  HearingRecordIncarcerationTerm,
  HearingRecordOffense,
  HearingRecordPanelMember,
  HearingRecordProceeding,
  HearingRecordRequirement,
  HearingSection,
  ProceedingHearingCaptureDto,
  ProceedingHearingCaptureUpsertRequest,
} from "./hearing-record.models";

/**
 * Author permission for the S2 capture PUT (#PHMS-HREC). The backend gates capture writes on this
 * existing proceeding-scoped record-author permission; the surface mirrors that so the read-only
 * state matches the server's gate (see {@link RoleService.hasAnyPermissionSignal}).
 */
const CAPTURE_AUTHOR_PERMISSION = "inmate_offense_record.author";

/**
 * Dedicated permission for the S5 decision POST (ADO #1508). Schedulers hold the capture author
 * permission for hearing-detail/document writes but must never record a hearing decision, so the
 * server gates the decision finalize on this separate permission and the decision-entry controls
 * key off the same code — hidden exactly when the server would refuse the POST.
 */
const DECISION_RECORD_PERMISSION = "phms.hearing_decision.record";

/** Debounce window for autosave; long enough to coalesce keystrokes, short enough to feel live. */
const AUTOSAVE_DEBOUNCE_MS = 800;

/**
 * At-hearing capture surface (#PHMS-HREC S4) — the "Suitability Hearing" live-entry tab.
 *
 * A panel enters suitability content *during* the proceeding (offense circumstances, insight /
 * remorse / accountability, plans, stakeholders, …) which autosaves (debounced, partial, idempotent)
 * to the S2 capture via {@link HearingRecordService.upsertCapture}. Read-only case context (identity,
 * custody, offenses, panel, readiness, next hearing) comes from the cohesive S1 record and is shown
 * in a Case Snapshot rail. Fact capture is kept visibly separate from adjudication: the decision is
 * always available for review and entry, then submitted through the dedicated S5 finalize endpoint
 * ({@link HearingRecordService.finalizeDecision}), never the capture upsert.
 *
 * Section tabs are driven from the {@link sections} config array (no hand-coded duplication); the
 * body renders only the single {@link activeSection} at a time. Inline conditional/required
 * affordances use computed signals (no rule engine). The
 * surface is read-only (autosave + finalize suppressed) when the actor lacks the author permission,
 * matching the server.
 */
@Component({
  selector: "phms-suitability-hearing",
  standalone: true,
  imports: [HearingPhaseBarDirective, CommonModule, FormsModule, UiLibraryModule, HearingTaskWorkspaceComponent],
  templateUrl: "./suitability-hearing.component.html",
  styleUrl: "./suitability-hearing.component.scss",
})
export class SuitabilityHearingComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private confirmationDialog: MatDialogRef<EzDialogComponent> | null = null;
  private loadRevision = 0;
  private readonly roles = inject(RoleService);
  private readonly appState = inject(BitsStateService);
  private readonly recordService = inject(HearingRecordService);
  private readonly contextStore = inject(HearingCycleContextStore, { optional: true });
  private readonly caseContextRefresh = inject(CaseContextRefreshService);
  readonly facilityText = createTerminologyUsageSignals(PHMS_FACILITY_TERMINOLOGY.suitability);
  readonly hearingText = createTerminologyUsageSignals(PHMS_HEARING_TERMINOLOGY.suitability);
  readonly decisionText = createTerminologyUsageSignals(PHMS_DECISION_TERMINOLOGY.suitability);

  /** The hosting proceeding. Setting it (re)loads the S1 record and the current S2 capture. */
  @Input({ required: true })
  set scheduledProceedingId(value: number | null) {
    this._proceedingId.set(value);
    if (value !== null && value > 0) {
      this.load(value);
    }
  }
  get scheduledProceedingId(): number | null {
    return this._proceedingId();
  }

  private readonly _proceedingId = signal<number | null>(null);
  private readonly _record = signal<HearingRecordDto | null>(null);

  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly saveError = signal<string | null>(null);
  readonly lastSavedAt = signal<Date | null>(null);
  readonly pendingSaveCount = signal(0);
  readonly capturePending = computed(() => this.pendingSaveCount() > 0 || this.isSaving());
  readonly captureFailed = computed(() => this.saveError() !== null);
  private failedCapture: ProceedingHearingCaptureUpsertRequest | null = null;
  readonly isConfirmingDecision = signal(false);

  /** Author permission gate; the form is read-only (autosave + finalize suppressed) when false. */
  readonly canAuthor = this.roles.hasAnyPermissionSignal(CAPTURE_AUTHOR_PERMISSION);
  /** Decision permission gate (ADO #1508); decision entry is hidden and finalize suppressed when false. */
  readonly canRecordDecision = this.roles.hasAnyPermissionSignal(DECISION_RECORD_PERMISSION);
  private readonly contactReader = this.roles.hasAnyPermissionSignal("victim_support_record.read");
  private readonly sensitiveReader = this.roles.hasAnyPermissionSignal("victim_support_record.read_sensitive");
  readonly canReadContacts = computed(() => this.contactReader() && this.sensitiveReader());
  private readonly canReadOfficials = this.roles.hasAnyPermissionSignal("workflow_runtime.read");
  private readonly officialsApi = inject(HearingOfficialAssignmentService);
  private readonly noticesApi = inject(HearingNoticeWorkspaceService);
  private readonly rosterOfficials = signal<HearingOfficialAssignment[]>([]);
  private readonly rosterParticipants = signal<NoticeParticipant[]>([]);
  readonly rosterLoading = signal(false);
  readonly rosterMessage = signal("");

  /** A recorded decision requires amendment; attendance and capture notes remain editable. */
  readonly hasFinalDecision = computed(() => {
    const outcome = this._record()?.decision?.outcome;
    return (outcome?.finding ?? "").trim().length > 0;
  });

  /** Capture editing follows record-author access independently of the immutable adjudication. */
  readonly canEdit = computed(
    () => this.canAuthor()
      && (this._proceedingId() ?? 0) > 0
      && this._record()?.scheduledProceedingId === this._proceedingId()
      && !this.isLoading() && !this.isFinalizing() && !this.isConfirmingDecision(),
  );

  /** Hearing-day controls (start/end) stay open to capture authors until the adjudication is recorded. */
  readonly canEditHearingDay = computed(() => this.canEdit() && !this.hasFinalDecision() && !this.decisionSubmitted());

  /** Decision entry additionally requires the dedicated decision permission (ADO #1508). */
  readonly canEditDecision = computed(() => this.canEditHearingDay() && this.canRecordDecision());

  /**
   * Whether the Decision section is offered at all: recorders always see it; other roles only see
   * the read-only view of an already-recorded decision (never an empty entry form).
   */
  readonly showDecisionSection = computed(() => this.canRecordDecision() || this.hasFinalDecision());

  /**
   * Which section is currently active (tab-driven). Only the active section's content is rendered
   * below the tab bar; selecting a different tab switches which section is shown. Defaults to the
   * first section so the surface opens on the live hearing record.
   */
  readonly activeSectionId = signal<string>("4-offense");

  /**
   * Shared, two-way bound capture model. A signal (not a plain object) so every section tab —
   * each dynamically instantiated by {@link https://material.angular.io ui-tab-group} — reads and
   * writes the same current object; switching tabs always loads the live value.
   */
  readonly formValues = signal<AtHearingFormValues>(createEmptyFormValues());

  /**
   * The section tab group, addressed for programmatic activation (Open Decision jumps to the
   * Decision tab). Typed structurally so the shell does not import the library component.
   */
  @ViewChild("tabGroupRef") private tabGroup?: {
    activeTabIndex: number;
    onTabChange(index: number): void;
  };

  /* --- Inline conditional/required drivers (computed signals; no rule engine). --- */
  private readonly offenseCircumstances = signal("");

  /** §4 required: circumstances of the offense must be entered. */
  readonly offenseRequiredIncomplete = computed(() => this.offenseCircumstances().trim().length === 0);

  /**
   * Hearing sections rendered as both progress chips and accordions, numbered sequentially 1-10
   * (section ids keep their original statutory-style keys for stability). Required-incomplete
   * flags are computed live from the relevant field drivers, so a chip's ★ clears the moment its
   * required content is entered.
   */
  readonly sections = computed<HearingSection[]>(() => {
    const offenseIncomplete = this.offenseRequiredIncomplete();
    // Identity, Metadata, and Insight tabs removed (#1458 / UI-refactor notes): identity context
    // lives on the embedded Case Snapshot rail, hearing metadata is covered by the header pills,
    // and Insight came off the simplified tab set. Offenses stays — its circumstances narrative is
    // required capture input. "Community Integration" is the renamed Plans section and
    // "In-Prison Violations" the refocused Criminal History section (same capture fields
    // underneath; section ids keep their statutory-style keys).
    const sections: HearingSection[] = [
      {
        id: "4-offense",
        number: "1",
        title: "Participation & discussion",
        requiredIncomplete: offenseIncomplete,
      },
    ];
    // ADO #1508: no decision entry for roles that cannot record a decision (e.g. schedulers).
    if (this.showDecisionSection()) {
      sections.push({
        id: "14-decision",
        number: "2",
        title: this.decisionText.decision(),
        requiredIncomplete: false,
      });
    }
    return sections;
  });

  /** A required field is not evidence that the other hearing topics were reviewed. */
  readonly captureGuidance = computed(() => this.hasFinalDecision() || this.decisionSubmitted()
    ? "Decision recorded · Hearing record is read-only"
    : this.offenseRequiredIncomplete() ? "Required: circumstances of the offense" : "Required hearing narrative entered");

  /** Lighter sections rendered as collapsible panels; each maps to a single narrative field. */
  readonly lightSectionConfigs = computed<Record<string, InputConfig[]>>(() => ({
    "1-metadata": [
      {
        name: "metadataNotes",
        type: EzFormInputType.TEXTAREA,
        label: this.hearingText.metadataNotes(),
        row: 1,
        width: "fill",
        textAreaRows: 2,
        placeholder: this.hearingText.logisticsPlaceholder(),
      },
    ],
    "2-identity": [
      {
        name: "identityNotes",
        type: EzFormInputType.TEXTAREA,
        label: "Identity confirmation notes",
        row: 1,
        width: "fill",
        textAreaRows: 2,
        placeholder: "Confirm identity, counsel present, interpreter needs.",
      },
    ],
    "5-criminal-history": [
      {
        name: "criminalHistorySummary",
        type: EzFormInputType.TEXTAREA,
        label: "Rules violation review",
        row: 1,
        width: "fill",
        textAreaRows: 4,
        floatLabel: "always",
        placeholder: this.facilityText.inCustodyConductPlaceholder(),
      },
    ],
    "6-institutional": [
      {
        name: "institutionalSummary",
        type: EzFormInputType.TEXTAREA,
        label: this.facilityText.behaviorSummary(),
        row: 1,
        width: "fill",
        textAreaRows: 4,
        floatLabel: "always",
      },
    ],
    "7-risk": [
      {
        name: "riskSummary",
        type: EzFormInputType.TEXTAREA,
        label: "Risk / CRA discussion",
        row: 1,
        width: "fill",
        textAreaRows: 4,
        floatLabel: "always",
      },
    ],
    "11-plans": [
      {
        name: "parolePlans",
        type: EzFormInputType.TEXTAREA,
        label: "Parole / release plans",
        row: 1,
        width: "fill",
        textAreaRows: 4,
        floatLabel: "always",
      },
    ],
    "12-stakeholders": [
      {
        name: "stakeholderParticipation",
        type: EzFormInputType.TEXTAREA,
        label: "Participation notes (optional)",
        floatLabel: "always",
        row: 1,
        width: "fill",
        textAreaRows: 3,
      },
    ],
  }));

  /**
   * Section catalogue that drives the tab group: each entry maps a statutory section to the section
   * component that renders its tab body. Narrative sections reuse a single generic component with
   * their own field config; §4 Offense and §14 Decision use dedicated components. Kept ordered 1-10
   * so the tab index matches the displayed number.
   */
  private readonly sectionDefs = computed<
    ReadonlyArray<{
      id: string;
      number: string;
      tabLabel: string;
      kind: "topic" | "offense" | "participants" | "decision";
      groups?: readonly HearingTopicGroup[];
      testId: string;
    }>
  >(() => [
    // Identity and Metadata removed (#1458): the embedded Case Snapshot rail + header pills carry
    // that context; Offense leads because its circumstances narrative is required capture input.
    {
      id: "4-offense",
      number: "1",
      tabLabel: "Participation & discussion",
      kind: "offense" as const,
      testId: "section-hearing-record",
    },
    // ADO #1508: the Decision tab (entry controls) is omitted for roles without the decision
    // permission unless a decision is already recorded, in which case it renders read-only.
    ...(this.showDecisionSection()
      ? [{
        id: "14-decision",
        number: "2",
        tabLabel: this.decisionText.decision(),
        kind: "decision" as const,
        testId: "section-14-decision",
      }]
      : []),
  ]);

  /**
   * The tab-group config. Built once (only stable signal/object references are captured, never their
   * values), so a keystroke never rebuilds the array and re-instantiates the active tab. Each tab's
   * body component receives the shared model + gates through `inputs` and reports changes through
   * `events`, so autosave, the required drivers and the decision finalize stay owned by this host.
   */
  /**
   * SDMF tab visibility (shared with the Pre-Hearing page): hidden whenever the admin-managed
   * SDMF feature flag is off. Reactive through the RoleService toggledFeatures signal.
   */

  readonly sectionTabs = computed<TabConfig[]>(() => {
    const tabs = this.sectionDefs().map((def): TabConfig => {
      if (def.kind === "offense") {
        return {
          label: def.tabLabel,
          component: HearingOffenseSectionComponent,
          inputs: {
            testId: def.testId,
            values: this.formValues,
            canEdit: this.canEdit,
            offenses: this.offenses,
            ageAtOffense: this.ageAtOffense,
            priorHearingOutcome: this.priorHearingOutcome,
            attendees: this.scheduledAttendees(),
            rosterLoading: this.rosterLoading(),
            rosterMessage: this.rosterMessage(),
            proceedingId: this._proceedingId(),
            canReadContacts: this.canReadContacts(),
            presence: this.attendancePresence,
            participationConfig: this.lightSectionConfigs()["12-stakeholders"],
          },
          events: {
            attendanceChange: (event: unknown) => this.onAttendanceChange(event as HearingAttendanceChange),
            inputChange: (event: unknown) => this.onInputChange(event as InputChangeEvent),
          },
        };
      }
      if (def.kind === "participants") {
        return {
          label: def.tabLabel,
          component: HearingParticipantsSectionComponent,
          inputs: {
            attendees: this.scheduledAttendees(),
            rosterLoading: this.rosterLoading(),
            rosterMessage: this.rosterMessage(),
            proceedingId: this._proceedingId(),
            canReadContacts: this.canReadContacts(),
            presence: this.attendancePresence,
            values: this.formValues,
            canEdit: this.canEdit,
            inputConfig: this.lightSectionConfigs()["12-stakeholders"],
          },
          events: {
            attendanceChange: (event: unknown) => this.onAttendanceChange(event as HearingAttendanceChange),
            inputChange: (event: unknown) => this.onInputChange(event as InputChangeEvent),
          },
        };
      }
      if (def.kind === "decision") {
        return {
          label: def.tabLabel,
          component: HearingDecisionSectionComponent,
          inputs: {
            testId: def.testId,
            decisionValues: this.decisionValues,
            canEdit: this.canEditDecision,
            canSubmitDecision: this.canSubmitDecision,
            isUnsuitable: this.isUnsuitable,
            isFinalizing: this.isFinalizing,
            decisionError: this.decisionError,
            decisionSubmitted: this.decisionSubmitted,
            findingOptions: this.findingOptions,
            proceedingId: this._proceedingId(),
            capturePending: this.capturePending,
            captureFailed: this.captureFailed,
          },
          events: {
            findingChange: (value: unknown) => this.onFindingChange(value as string),
            rationaleChange: (value: unknown) => this.onRationaleChange(value as string),
            denialLengthChange: (value: unknown) => this.onDenialLengthChange(value as string),
            submit: () => this.submitDecision(),
          },
        };
      }
      return {
        label: def.tabLabel,
        component: HearingTopicSectionComponent,
        inputs: {
          testId: def.testId,
          groups: def.groups ?? [],
          violations: def.id === "conduct-risk" ? this.inPrisonViolations() : null,
          values: this.formValues,
          canEdit: this.canEdit,
        },
        events: {
          inputChange: (event: unknown) => this.onInputChange(event as InputChangeEvent),
        },
      };
    });

    // The shell owns the shared SDMF destination.
    tabs.push({
      label: "Documents",
      component: PhmsCaseDocumentsComponent,
      inputs: {
        incarceratedPersonId: this.incarceratedPersonId(),
        subjectName: this.subjectName(),
      },
    });
    return tabs;
  });

  /* --- Decision entry and finalize state. --- */
  readonly isFinalizing = signal(false);
  readonly decisionError = signal<string | null>(null);
  readonly decisionSubmitted = signal(false);
  readonly findingOptions = FINDING_OPTIONS;

  /** Two-way bound decision model (separate from the capture form model). */
  decisionValues: DecisionFormValues = createEmptyDecisionValues();

  /**
   * The decision finalizes only when all required values are complete. Validation drivers are signals —
   * `decisionValues` alone would not re-trigger the computed when the rationale is typed after the
   * finding is picked, leaving Submit stuck disabled.
   */
  readonly decisionFinding = signal("");
  private readonly decisionRationale = signal("");
  private readonly decisionDenialLength = signal("");
  readonly canSubmitDecision = computed(
    () => this.canEditDecision()
      && !this.capturePending() && !this.captureFailed()
      && this.findingOptions.some(option => option.value === this.decisionFinding())
      && this.decisionRationale().trim().length > 0
      && (this.decisionFinding() !== "unsuitable" || positiveDecisionYears(this.decisionDenialLength()) !== null),
  );
  readonly isUnsuitable = computed(() => this.decisionFinding() === "unsuitable");

  /* --- Read-only context derived from the S1 record (Case Snapshot rail + offense table). --- */
  private readonly context = computed(() => this._record()?.context ?? null);
  readonly demographics = computed(() => this.context()?.demographics ?? null);
  /** Person id for the embedded Case Snapshot rail (#1458); null until the S1 record resolves. */
  readonly incarceratedPersonId = computed(() => this._record()?.incarceratedPersonId ?? null);
  readonly proceeding = computed<HearingRecordProceeding | null>(() => this.context()?.proceeding ?? null);
  readonly offenses = computed<HearingRecordOffense[]>(() => this.context()?.offenses ?? []);
  readonly inPrisonViolations = computed(() => this.offenses().filter((offense) =>
    /violation/i.test(offense.recordCategoryCode ?? "") || /rules violation|\brvr\b/i.test(offense.summary ?? ""),
  ));
  readonly attendancePresence = signal<Record<string, boolean>>({});
  readonly scheduledAttendees = computed<HearingAttendee[]>(() => {
    const panel = this.rosterOfficials().map((member): HearingAttendee => ({
      key: `official:${member.officialUserId}`,
      name: member.officialDisplayName,
      role: this.hearingText.official(),
      source: "panel",
      isPresiding: member.isPresiding ?? false,
    }));
    const participants = this.rosterParticipants().filter(participant => participant.isActive).map((participant): HearingAttendee => ({
      // Notice-roster IDs and legacy scheduling-resource IDs belong to different identity spaces.
      key: `notice-participant:${participant.id}`,
      name: participant.displayName,
      role: this.attendeeRoleLabel(participant.roleCode) || "Participant",
      source: "participant",
    }));
    return [...panel, ...participants];
  });

  private attendeeRoleLabel(roleCode: string | null | undefined): string {
    const normalized = (roleCode ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");
    if (["commissioner", "presiding_commissioner", "deputy_commissioner", "parole_official"].includes(normalized)) {
      return this.hearingText.official();
    }
    return titleCase(roleCode);
  }
  readonly hearingStartedAt = signal<Date | null>(null);
  readonly hearingEndedAt = signal<Date | null>(null);
  readonly hearingSessionStatus = computed(() =>
    this.hearingEndedAt() ? "Ended" : this.hearingStartedAt() ? "In progress" : "Not started",
  );
  readonly ageAtOffense = computed(() => this._record()?.derived?.ageAtOffense ?? null);
  readonly priorHearingOutcome = computed(() => {
    const hearings = [...(this.context()?.priorHearings ?? [])]
      .sort((left, right) => (right.hearingDate ?? "").localeCompare(left.hearingDate ?? ""));
    const outcome = hearings[0]?.decisionCode;
    if (outcome) {
      return titleCase(outcome);
    }
    const legacy = this.offenses().find((offense) => /hearing_history/i.test(offense.recordCategoryCode ?? ""));
    return titleCase(legacy?.dispositionCode) || "Not recorded";
  });
  readonly hasOffenses = computed(() => this.offenses().length > 0);

  readonly subjectName = computed(() => {
    const demo = this.demographics();
    const full = (demo?.fullName ?? "").trim();
    if (full) {
      return full;
    }
    return [demo?.firstName, demo?.lastName]
      .map((part) => (part ?? "").trim())
      .filter((part) => part.length > 0)
      .join(" ");
  });

  readonly proceedingNumber = computed(() => {
    const proceeding = this.proceeding();
    return (proceeding?.title ?? "").trim() || `PHMS-${this._proceedingId() ?? ""}`;
  });
  // No hearing-type pill: the "Subsequent Parole Hearing" text came off the header (UI-refactor
  // notes); the type still shows on the Case Snapshot rail's next-hearing context.
  readonly modality = computed(() => titleCase(this.proceeding()?.format) || "Video");
  readonly nextHearingStatus = computed(() => (this.proceeding()?.statusLabel ?? "").trim() || "Scheduled");
  readonly panel = computed<HearingRecordPanelMember[]>(() => this.proceeding()?.panel ?? []);
  readonly requirements = computed<HearingRecordRequirement[]>(() => this.proceeding()?.requirements ?? []);
  readonly requirementsComplete = computed(() => this.proceeding()?.requirementsComplete ?? 0);
  readonly requirementsTotal = computed(() => this.proceeding()?.requirementsTotal ?? this.requirements().length);

  /** Current custody term for the snapshot rail. */
  readonly currentTerm = computed<HearingRecordIncarcerationTerm | null>(() => {
    const terms = this.context()?.incarcerationHistory?.terms ?? [];
    return terms.find((term) => term.isCurrent) ?? terms[0] ?? null;
  });
  readonly admitDate = computed(() => this.context()?.incarcerationHistory?.admitDate ?? this.demographics()?.admitDate ?? null);
  readonly eligibilityDate = computed(
    () => this.context()?.incarcerationHistory?.eligibilityDate ?? this.demographics()?.eligibilityDate ?? null,
  );
  readonly releaseDate = computed(
    () => this.context()?.incarcerationHistory?.projectedReleaseDate ?? this.demographics()?.projectedReleaseDate ?? null,
  );

  /** Autosave pipeline: each field change debounces, then upserts the changed field to the S2 capture. */
  private readonly saveRequests = new Subject<{ proceedingId: number; revision: number; payload: ProceedingHearingCaptureUpsertRequest }>();

  readonly pageTitle = this.hearingText.title;

  constructor() {
    effect((onCleanup) => {
      const id = this._proceedingId();
      const readOfficials = this.canReadOfficials();
      const readParticipants = this.canReadContacts();
      this.rosterOfficials.set([]);
      this.rosterParticipants.set([]);
      this.rosterMessage.set("");
      this.rosterLoading.set(id !== null && id > 0);
      if (!id || id <= 0) return;
      // Read the same live sources as Pre-Hearing on phase entry, independently of the context cache.
      const request = forkJoin({
        officials: readOfficials ? this.officialsApi.getMembers(id).pipe(catchError(() => of(null))) : of([]),
        workspace: readParticipants ? this.noticesApi.get(id).pipe(catchError(() => of(null))) : of({ participants: [] }),
      }).subscribe(({ officials, workspace }) => {
        this.rosterOfficials.set(officials ?? []);
        this.rosterParticipants.set(workspace?.participants ?? []);
        this.rosterLoading.set(false);
        this.rosterMessage.set(officials === null || workspace === null
          ? "The attendance roster could not be fully loaded. Reload the page to try again."
          : !readParticipants || !readOfficials ? "Some roster details are restricted by your permissions." : "");
      });
      onCleanup(() => request.unsubscribe());
    });
    this.destroyRef.onDestroy(() => this.confirmationDialog?.close(false));
    const settledSaveRequests = this.saveRequests.pipe(debounceTime(AUTOSAVE_DEBOUNCE_MS));
    this.saveRequests.pipe(
      buffer(settledSaveRequests),
      map(requests => requests.filter(request => request.revision === this.loadRevision)),
      filter(requests => requests.length > 0),
      concatMap(requests => {
        const { proceedingId, revision } = requests[0];
        if (revision !== this.loadRevision) return of(null);
        if (!this.canEdit()) {
          this.pendingSaveCount.update(count => Math.max(0, count - requests.length));
          return of(null);
        }
        // Retry failed fields alongside newer edits; a successful unrelated save must not
        // incorrectly declare an earlier failed field saved or allow decision finalization.
        const payload = mergeCaptureRequests([...(this.failedCapture ? [this.failedCapture] : []), ...requests.map(request => request.payload)]);
        this.isSaving.set(true);
        return this.recordService.upsertCapture(proceedingId, payload).pipe(
          tap(result => {
            if (revision !== this.loadRevision) return;
            this.failedCapture = null;
            this.saveError.set(null);
            this.lastSavedAt.set(new Date());
            this.contextStore?.updateCapture(result);
          }),
          catchError((error: unknown) => {
            if (revision === this.loadRevision) {
              this.failedCapture = payload;
              this.saveError.set(messageFromError(error, "Hearing changes were not saved. Retry the save before recording a decision.", this.hearingText.permissionDenied()));
            }
            return of(null);
          }),
          finalize(() => {
            if (revision === this.loadRevision) {
              this.isSaving.set(false);
              this.pendingSaveCount.update(count => Math.max(0, count - requests.length));
            }
          }),
        );
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  private queueCapture(payload: ProceedingHearingCaptureUpsertRequest): void {
    const proceedingId = this._proceedingId();
    if (!this.canEdit() || !proceedingId) return;
    this.pendingSaveCount.update(count => count + 1);
    this.saveRequests.next({ proceedingId, revision: this.loadRevision, payload });
  }

  retryCaptureSave(): void {
    if (this.canEdit() && !this.capturePending() && this.failedCapture) this.queueCapture(this.failedCapture);
  }

  /**
   * Save-draft action. Autosave already persists each field on change, so this flushes the two
   * required narratives (offense circumstances + insight) explicitly, giving the actor a deliberate
   * "saved" affordance without a distinct draft endpoint.
   */
  saveDraft(): void {
    if (!this.canEdit()) {
      return;
    }
    const values = this.formValues();
    this.queueCapture({
      narratives: {
        offenseCircumstances: toStringOrEmpty(values.offenseCircumstances),
        insightCausativeFactors: toStringOrEmpty(values.insightCausativeFactors),
      },
    });
  }

  /** Make the target section the active/rendered tab (both programmatically and from a tab click). */
  selectSection(sectionId: string): void {
    if (["release-support", "conduct-risk", "participants"].includes(sectionId)) sectionId = "4-offense";
    this.activeSectionId.set(sectionId);
    const index = this.sectionDefs().findIndex((def) => def.id === sectionId);
    if (index >= 0) {
      this.tabGroup?.onTabChange(index);
    }
  }

  isSectionActive(sectionId: string): boolean {
    return this.activeSectionId() === (["release-support", "conduct-risk", "participants"].includes(sectionId) ? "4-offense" : sectionId);
  }

  onTaskSelected(index: number): void {
    this.activeSectionId.set(this.sectionDefs()[index]?.id
      ?? (this.sectionTabs()[index]?.component === PhmsCaseDocumentsComponent ? "documents" : "analysis"));
  }

  nextTopic(): void {
    const index = this.sectionDefs().findIndex(section => section.id === this.activeSectionId());
    if (index >= 0 && index < this.sectionDefs().length - 1) {
      this.selectSection(this.sectionDefs()[index + 1].id);
    }
  }

  /**
   * Single-field change handler for the capture forms. Keeps the inline conditional/required drivers
   * in sync and enqueues a partial upsert carrying only the changed field, so autosave never
   * overwrites an untouched field on the partial-upsert contract.
   */
  onInputChange(event: InputChangeEvent): void {
    if (!this.canEdit()) return;
    switch (event.name) {
      case "offenseCircumstances":
        this.offenseCircumstances.set(String(event.value ?? ""));
        break;
      default:
        break;
    }
    if (!this.canAuthor()) {
      return;
    }
    const payload = this.toUpsertPayload(event.name, event.value);
    if (payload) {
      this.queueCapture(payload);
    }
  }

  markHearingStarted(): void {
    if (!this.canEditHearingDay() || this.hearingStartedAt()) {
      return;
    }
    this.setSessionTimestamp("hearingStartedUtc", new Date());
  }

  markHearingEnded(): void {
    if (!this.canEditHearingDay() || !this.hearingStartedAt() || this.hearingEndedAt()) {
      return;
    }
    this.setSessionTimestamp("hearingEndedUtc", new Date());
  }

  onAttendanceChange(event: HearingAttendanceChange): void {
    if (!this.canEdit() || this.rosterLoading() || !this.scheduledAttendees().some(attendee => attendee.key === event.key)
      || (event.present !== null && typeof event.present !== "boolean")) return;
    const next = { ...this.attendancePresence() };
    if (event.present === null) delete next[event.key];
    else next[event.key] = event.present;
    this.attendancePresence.set(next);
    const serialized = JSON.stringify(next);
    this.formValues.update((values) => ({ ...values, attendeePresence: serialized }));
    this.queueCapture({ narratives: { attendeePresence: serialized } });
  }

  private setSessionTimestamp(name: "hearingStartedUtc" | "hearingEndedUtc", date: Date): void {
    const value = date.toISOString();
    this.formValues.update((values) => ({ ...values, [name]: value }));
    if (name === "hearingStartedUtc") {
      this.hearingStartedAt.set(date);
    } else {
      this.hearingEndedAt.set(date);
    }
    this.queueCapture({ narratives: { [name]: value } });
  }

  /** Maps a single changed capture field to a minimal partial-upsert payload (narrative key). */
  private toUpsertPayload(name: string, value: unknown): ProceedingHearingCaptureUpsertRequest | null {
    switch (name) {
      case "offenseUnderStress":
      case "signsOfRemorse":
        return { narratives: { [name]: value ? "true" : "false" } };
      case "offenseCircumstances":
      case "offenseMotivation":
      case "offenseNexus":
      case "insightCausativeFactors":
      case "acceptanceOfResponsibility":
      case "remorseVictimImpact":
      case "metadataNotes":
      case "identityNotes":
      case "criminalHistorySummary":
      case "institutionalSummary":
      case "riskSummary":
      case "parolePlans":
      case "stakeholderParticipation":
      case "hearingStartedUtc":
      case "hearingEndedUtc":
      case "attendeePresence":
        return { narratives: { [name]: toStringOrEmpty(value) } };
      default:
        return null;
    }
  }

  /** Navigate directly to the always-available decision entry. */
  proceedToDecision(): void {
    this.selectSection("14-decision");
  }

  /** Track the finding selection (drives the conditional denial-length field + submit enablement). */
  onFindingChange(value: string): void {
    if (!this.canEditDecision()) return;
    this.decisionFinding.set(value);
    this.decisionValues.finding = value;
  }

  /** Mirror the rationale into validation state so Finalize enables as soon as it is complete. */
  onRationaleChange(value: string): void {
    if (!this.canEditDecision()) return;
    this.decisionValues.rationale = value ?? "";
    this.decisionRationale.set(value ?? "");
  }

  onDenialLengthChange(value: string): void {
    if (!this.canEditDecision()) return;
    this.decisionValues.denialLengthYears = value ?? "";
    this.decisionDenialLength.set(value ?? "");
  }

  /**
   * Submit the §14 decision through the dedicated S5 finalize endpoint — never the capture upsert.
   * Suppressed unless the actor can author; mirrors the server's permission gate.
   */
  submitDecision(): void {
    const proceedingId = this._proceedingId();
    if (proceedingId === null || proceedingId <= 0 || !this.canSubmitDecision()) {
      return;
    }
    const finding = this.decisionFinding();
    const specialConditions = splitLines(this.decisionValues.specialConditions);
    const payload: HearingDecisionFinalizeRequest = {
      finding,
      decisionRationale: (this.decisionValues.rationale ?? "").trim(),
      decidedByUserId: toPositiveInt(this.appState.idSignal()),
      captureSequence: 1,
      denialLengthYears: finding === "unsuitable" ? positiveDecisionYears(this.decisionValues.denialLengthYears) : null,
      specialConditions: specialConditions.length > 0 ? specialConditions : null,
    };

    const revision = this.loadRevision;
    const escape = (value: string) => value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
    const findingLabel = this.findingOptions.find(option => option.value === finding)?.viewValue ?? finding;
    const data: EzDialogData = {
      heading: "Record decision", headerStyle: "info",
      question: [`<p>Record this decision? Later decision changes require an authorized amendment. Attendance and hearing notes remain editable.</p>`,
        `<p><strong>Finding:</strong> ${escape(findingLabel)}${payload.denialLengthYears ? `<br><strong>Denial length:</strong> ${payload.denialLengthYears} years` : ""}</p>`,
        `<p><strong>Rationale:</strong></p><p style="white-space: pre-wrap; overflow-wrap: anywhere">${escape(payload.decisionRationale ?? "")}</p>`,
        `<p><strong>Special conditions:</strong></p><p style="white-space: pre-wrap; overflow-wrap: anywhere">${escape((payload.specialConditions ?? []).join("\n") || "None entered")}</p>`].join(""),
      buttons: [{ text: "Cancel", action: ref => ref.close(false) }, { text: "Confirm & record decision", action: ref => ref.close(true) }],
    };
    this.isConfirmingDecision.set(true);
    this.confirmationDialog = this.dialog.open(EzDialogComponent, {
      width: "40rem", maxWidth: "calc(100vw - 2rem)", maxHeight: "85vh", autoFocus: "first-tabbable", restoreFocus: true,
      ariaLabel: data.heading, data,
    });
    this.confirmationDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(confirmed => {
      this.confirmationDialog = null;
      this.isConfirmingDecision.set(false);
      if (confirmed === true && revision === this.loadRevision && this.canSubmitDecision()) this.finalizeConfirmedDecision(proceedingId, revision, payload);
    });
  }

  private finalizeConfirmedDecision(proceedingId: number, revision: number, payload: HearingDecisionFinalizeRequest): void {
    this.isFinalizing.set(true);
    this.decisionError.set(null);
    this.recordService.finalizeDecision(proceedingId, payload).pipe(
      catchError((error: unknown) => {
        if (revision === this.loadRevision) this.decisionError.set(messageFromError(error, this.decisionText.finalizeFailed(), this.hearingText.permissionDenied()));
        return of(null);
      }),
      finalize(() => { if (revision === this.loadRevision) this.isFinalizing.set(false); }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(result => {
      if (result && revision === this.loadRevision) {
        this.decisionSubmitted.set(true);
        this.contextStore?.refresh().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        // #1509: the Case Snapshot rail is mounted in the hearing-cycle shell, not under this route;
        // tell it to re-read so it stops showing "Scheduled" once the decision is recorded.
        this.caseContextRefresh.requestRefresh({
          reason: "hearing-decision-recorded",
          incarceratedPersonId: this.incarceratedPersonId(),
          scheduledProceedingId: proceedingId,
        });
      }
    });
  }

  private load(scheduledProceedingId: number): void {
    this.loadRevision++;
    this.confirmationDialog?.close(false);
    this.pendingSaveCount.set(0);
    this.isSaving.set(false);
    this.isFinalizing.set(false);
    this.failedCapture = null;
    this.isLoading.set(true);
    this.saveError.set(null);

    const recordRequest = this.contextStore?.load(scheduledProceedingId)
      ?? this.recordService.getRecord(scheduledProceedingId);

    recordRequest
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((record) => {
        this.isLoading.set(false);
        this._record.set(record);
        this.applyCapture(record?.captured?.capture ?? null);
        this.applyDecision(record?.decision?.outcome ?? null);
      });
  }

  /** Hydrate the decision tab from the finalized S1 decision block and lock it against re-entry. */
  private applyDecision(outcome: HearingDecisionOutcome | null): void {
    const finding = (outcome?.finding ?? "").trim().toLowerCase();
    const rationale = (outcome?.rationale ?? "").trim();
    const denialLength = outcome?.denialLengthYears != null ? String(outcome.denialLengthYears) : "";
    Object.assign(this.decisionValues, {
      finding,
      rationale,
      specialConditions: (outcome?.specialConditions ?? []).join("\n"),
      denialLengthYears: denialLength,
    });
    this.decisionFinding.set(finding);
    this.decisionRationale.set(rationale);
    this.decisionDenialLength.set(denialLength);
    this.decisionSubmitted.set(finding.length > 0);
  }

  /** Hydrate the form model + conditional drivers from an existing capture (S2). */
  private applyCapture(capture: ProceedingHearingCaptureDto | null): void {
    if (!capture) {
      const empty = createEmptyFormValues();
      this.formValues.set(empty);
      this.syncDrivers(empty);
      return;
    }
    const narratives = parseNarratives(capture.narrativesJson);
    const values: AtHearingFormValues = {
      offenseCircumstances: narratives["offenseCircumstances"] ?? "",
      offenseMotivation: narratives["offenseMotivation"] ?? "",
      offenseUnderStress: narratives["offenseUnderStress"] === "true",
      offenseNexus: narratives["offenseNexus"] ?? "",
      insightCausativeFactors: narratives["insightCausativeFactors"] ?? "",
      acceptanceOfResponsibility: narratives["acceptanceOfResponsibility"] ?? "",
      signsOfRemorse: narratives["signsOfRemorse"] === "true",
      remorseVictimImpact: narratives["remorseVictimImpact"] ?? "",
      metadataNotes: narratives["metadataNotes"] ?? "",
      identityNotes: narratives["identityNotes"] ?? "",
      criminalHistorySummary: narratives["criminalHistorySummary"] ?? "",
      institutionalSummary: narratives["institutionalSummary"] ?? "",
      riskSummary: narratives["riskSummary"] ?? "",
      parolePlans: narratives["parolePlans"] ?? "",
      stakeholderParticipation: narratives["stakeholderParticipation"] ?? "",
      hearingStartedUtc: narratives["hearingStartedUtc"] ?? "",
      hearingEndedUtc: narratives["hearingEndedUtc"] ?? "",
      attendeePresence: narratives["attendeePresence"] ?? "",
    };
    this.formValues.set(values);
    this.syncDrivers(values);
  }

  private syncDrivers(values: AtHearingFormValues): void {
    this.offenseCircumstances.set(values.offenseCircumstances);
    this.hearingStartedAt.set(parseDateOrNull(values.hearingStartedUtc));
    this.hearingEndedAt.set(parseDateOrNull(values.hearingEndedUtc));
    this.attendancePresence.set(parseBooleanRecord(values.attendeePresence));
  }
}

function createEmptyFormValues(): AtHearingFormValues {
  return {
    offenseCircumstances: "",
    offenseMotivation: "",
    offenseUnderStress: false,
    offenseNexus: "",
    insightCausativeFactors: "",
    acceptanceOfResponsibility: "",
    signsOfRemorse: false,
    remorseVictimImpact: "",
    metadataNotes: "",
    identityNotes: "",
    criminalHistorySummary: "",
    institutionalSummary: "",
    riskSummary: "",
    parolePlans: "",
    stakeholderParticipation: "",
    hearingStartedUtc: "",
    hearingEndedUtc: "",
    attendeePresence: "",
  };
}

function mergeCaptureRequests(
  requests: ProceedingHearingCaptureUpsertRequest[],
): ProceedingHearingCaptureUpsertRequest {
  return requests.reduce<ProceedingHearingCaptureUpsertRequest>((merged, request) => ({
    ...merged,
    ...request,
    narratives: {
      ...(merged.narratives ?? {}),
      ...(request.narratives ?? {}),
    },
  }), {});
}

function parseDateOrNull(value: string): Date | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function parseBooleanRecord(value: string): Record<string, boolean> {
  if (!value) {
    return {};
  }
  try {
    const parsed = JSON.parse(value) as Record<string, unknown>;
    return Object.fromEntries(Object.entries(parsed).map(([key, present]) => [key, present === true]));
  } catch {
    return {};
  }
}

function parseNarratives(json: string | null | undefined): Record<string, string> {
  if (!json) {
    return {};
  }
  try {
    const parsed = JSON.parse(json);
    if (parsed && typeof parsed === "object") {
      const out: Record<string, string> = {};
      for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
        out[key] = value == null ? "" : String(value);
      }
      return out;
    }
  } catch {
    // Malformed narratives blob — treat as empty rather than failing the surface.
  }
  return {};
}

function toStringOrEmpty(value: unknown): string {
  if (value == null) {
    return "";
  }
  return String(value).trim();
}

function titleCase(value: string | null | undefined): string {
  const text = (value ?? "").trim();
  if (!text) {
    return "";
  }
  return text
    .split(/[\s_-]+/)
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function splitLines(value: string | null | undefined): string[] {
  return (value ?? "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function toPositiveInt(value: unknown): number | null {
  const parsed = typeof value === "number" ? value : Number.parseInt(String(value ?? ""), 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function messageFromError(error: unknown, fallback: string, permissionDeniedMessage: string): string {
  if (error && typeof error === "object" && "status" in error) {
    const status = (error as { status?: unknown }).status;
    if (status === 403) {
      return permissionDeniedMessage;
    }
  }
  return fallback;
}

function positiveDecisionYears(value: string): number | null {
  const text = String(value ?? "").trim();
  const years = Number(text);
  return /^[1-9]\d*$/.test(text) && Number.isInteger(years) && years <= 2147483647 ? years : null;
}
